import { useState, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";

import { appKit } from "../lib/appKit";
import { CONTROLLER_ADDRESS } from "../config/configContracts";
import { calculateAmounts } from "../utils/calculateAmounts";
import { useDebounce } from "../hooks/useDebounce";

import BasePage from "../components/BasePage";
import AsidePanel from "../components/DApp/AsidePanel";
import TransactionModal from "../modals/TransactionModal";
import TabsContainer from "../components/DApp/central/TabsContainer";

import BGImage from "../assets/img/dAppbg.webp";
import { AppState } from "../types/types";

const DEFAULT_RATIO = 0.5;

const DApp: React.FC = () => {
  const [accountKitUser, setAccountKitUser] = useState<string | null>(null);
  const [balanceRefresh, setBalanceRefresh] = useState(0);
  const [globalLoading, setGlobalLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);

  const [modalTx, setModalTx] = useState({
    txStatus: null as "pending" | "confirmed" | "failed" | null,
    txHash: null as string | null,
  });

  const [sliderRatio, setSliderRatio] = useState(DEFAULT_RATIO);
  const debouncedRatio = useDebounce<number>(sliderRatio, 200);

  const [appState, setAppState] = useState<AppState>({
    userAddress: null,
    amountNAU: 0,
    balance: "0",
    conversionRatio: DEFAULT_RATIO,
    convertedAmountNAUY: 0,
    convertedAmountNAUN: 0,
    txHash: null,
    txStatus: null,
    loading: false,
    contractAddress: CONTROLLER_ADDRESS,
    error: null,
  });

  useEffect(() => {
    (async () => {
      try {
        const info = await appKit.getWalletInfo();
        if (info?.address && typeof info.address === "string") {
          setAccountKitUser(info.address);
        }
      } finally {
        setGlobalLoading(false);
      }
    })();
  }, []);

  useEffect(() => {
    if (accountKitUser) {
      setAppState((prev) => ({ ...prev, userAddress: accountKitUser }));
    }
  }, [accountKitUser]);

  useEffect(() => {
    if (appState.userAddress) {
      window.postMessage(
        { type: "SYNC_USER_ADDRESS", userAddress: appState.userAddress },
        "*"
      );
    }
  }, [appState.userAddress]);

  useEffect(() => {
    const fetchConverted = async () => {
      const X = appState.amountNAU;

      if (X <= 0) {
        setAppState((prev) => ({
          ...prev,
          conversionRatio: debouncedRatio,
          convertedAmountNAUY: 0,
          convertedAmountNAUN: 0,
          error: null,
        }));
        return;
      }

      const result = await calculateAmounts(X, debouncedRatio);

      if (result) {
        setAppState((prev) => ({
          ...prev,
          conversionRatio: debouncedRatio,
          convertedAmountNAUY: result.amountNAUY,
          convertedAmountNAUN: result.amountNAUN,
          error: null,
        }));
      } else {
        setAppState((prev) => ({
          ...prev,
          convertedAmountNAUY: 0,
          convertedAmountNAUN: 0,
          error: "⚠️ Price fetch failed.",
        }));
      }
    };

    fetchConverted();
  }, [appState.amountNAU, debouncedRatio]);

  const resetAppState = useCallback(() => {
    setAppState((prev) => ({
      ...prev,
      amountNAU: 0,
      conversionRatio: DEFAULT_RATIO,
      convertedAmountNAUY: 0,
      convertedAmountNAUN: 0,
      loading: false,
      error: null,
    }));
    setSliderRatio(DEFAULT_RATIO);
  }, []);

  const onTransactionSuccess = useCallback(() => {
    setBalanceRefresh((b) => b + 1);
    resetAppState();
  }, [resetAppState]);

  if (globalLoading) {
    return (
      <div className="w-screen h-screen flex items-center justify-center bg-[#031B34] text-white">
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.5 }}
        >
          <p className="text-lg font-semibold animate-pulse">
            Loading your dApp...
          </p>
        </motion.div>
      </div>
    );
  }

  return (
    <BasePage
      title="Wallet - UcWaves"
      description="Manage your wallet and tokens in real-time."
      keywords="Wallet, UcWaves, crypto, token, blockchain"
    >
      <main className="relative flex flex-col md:flex-row pt-16 md:pt-20 min-h-screen">
        <AsidePanel
          appState={appState}
          balanceRefresh={balanceRefresh}
          setBalanceRefresh={setBalanceRefresh}
          setAccountKitUser={setAccountKitUser}
          setAppState={setAppState}
        />

        <div className="fixed top-28 right-2 md:top-20 md:right-0 z-[110]">
          <motion.div
            initial={{ opacity: 1, scale: 0.96, y: -8 }}
            animate={{ opacity: 1, scale: 1.4, y: 0 }}
            transition={{ duration: 0.5, ease: "easeOut" }}
          />
        </div>

        <section
          className="relative w-full flex-1 flex flex-col items-center justify-center bg-cover bg-center bg-no-repeat"
          style={{ backgroundImage: `url(${BGImage})` }}
        >
          <TabsContainer
            appState={appState}
            setAppState={setAppState}
            sliderRatio={sliderRatio}
            setSliderRatio={setSliderRatio}
            onTransactionSuccess={onTransactionSuccess}
            setShowModal={setShowModal}
            setModalTx={setModalTx}
            setBalanceRefresh={setBalanceRefresh}
          />
        </section>
      </main>

      <AnimatePresence>
        {showModal && (
          <TransactionModal
            txStatus={modalTx.txStatus}
            txHash={modalTx.txHash}
            userAddress={appState.userAddress}
            closeModal={() => {
              setShowModal(false);
              setTimeout(() => {
                setModalTx({ txStatus: null, txHash: null });
              }, 300);
            }}
          />
        )}
      </AnimatePresence>
    </BasePage>
  );
};

export default DApp;
